import {
  DefaultCountry,
  DefaultLanguage,
  SupportedCountry,
  SupportedLanguage,
} from '../../utils/enums'

export enum TypeOfTerms {
  TermsAndConditions = 'terms-and-conditions',
  TermsOfUse = 'terms-of-use',
}

interface TermsAndConditions {
  pdf: string | undefined
  terms: string
}

type TermsObject = Record<
  SupportedCountry,
  Partial<Record<SupportedLanguage, () => Promise<TermsAndConditions>>>
>

const createToS = (
  locale: string,
) => async (): Promise<TermsAndConditions> => ({
  pdf: undefined,
  terms: (
    await import(/* webpackMode: "lazy-once" */ `./terms-of-use/${locale}.ts`)
  ).terms,
})

const createGTCoS = (locale: string, pdf: string) => async () => ({
  pdf: (
    await import(
      /* webpackMode: "lazy-once" */ `./terms-and-conditions/pdf/${pdf}.pdf`
    )
  ).default,
  terms: (
    await import(
      /* webpackMode: "lazy-once" */ `./terms-and-conditions/${locale}.ts`
    )
  ).terms,
})

const termsOfUse: TermsObject = {
  AU: { en: createToS('en-AU') },
  DE: { en: createToS('en-DE') },
  ID: { en: createToS('en-ID') },
  IN: { en: createToS('en-IN') },
  KR: {
    en: createToS('en-KR'),
    ko: createToS('ko-KR'),
  },
  MY: { en: createToS('en-MY') },
  NZ: { en: createToS('en-NZ') },
  PH: { en: createToS('en-PH') },
  SG: { en: createToS('en-SG') },
  TH: {
    en: createToS('en-TH'),
    th: createToS('th-TH'),
  },
  TW: {
    en: createToS('en-TW'),
    'zh-TW': createToS('zh-TW'),
  },
  VN: {
    en: createToS('en-VN'),
    vi: createToS('vi-VN'),
  },
}

const termsAndConditions: TermsObject = {
  AU: { en: createGTCoS('en-AU', 'GTCoS') },
  DE: { en: createGTCoS('en-DE', 'GTCoS') },
  ID: { en: createGTCoS('en-ID', 'GTCoS') },
  IN: { en: createGTCoS('en-IN', 'GTCoS') },
  KR: {
    en: createGTCoS('en-KR', 'GTCoS'),
    ko: createGTCoS('ko-KR', 'GTCoS_ko-KR'),
  },
  MY: { en: createGTCoS('en-MY', 'GTCoS') },
  NZ: { en: createGTCoS('en-NZ', 'GTCoS') },
  PH: { en: createGTCoS('en-PH', 'GTCoS') },
  SG: { en: createGTCoS('en-SG', 'GTCoS') },
  TH: {
    en: createGTCoS('en-TH', 'GTCoS_en-TH'),
    th: createGTCoS('th-TH', 'GTCoS_th-TH'),
  },
  TW: {
    en: createGTCoS('en-TW', 'GTCoS'),
    'zh-TW': createGTCoS('zh-TW', 'GTCoS_zh-TW'),
  },
  VN: {
    en: createGTCoS('en-VN', 'GTCoS'),
    vi: createGTCoS('vi-VN', 'GTCoS_vi-VN'),
  },
}

export const terms: Record<TypeOfTerms, TermsObject> = {
  [TypeOfTerms.TermsAndConditions]: termsAndConditions,
  [TypeOfTerms.TermsOfUse]: termsOfUse,
}

export const getTerms = (
  key: TypeOfTerms,
  language: SupportedLanguage = DefaultLanguage,
  country: SupportedCountry = DefaultCountry,
) => {
  if (!terms[key].hasOwnProperty(country)) {
    country = DefaultCountry
  }

  if (!terms[key][country].hasOwnProperty(language)) {
    language = DefaultLanguage
  }

  if (terms[key][country][language] !== undefined) {
    return terms[key][country][language]!()
  } else {
    throw new Error('Something went terribly wrong')
  }
}
